/**
 * PMProjectCard - Project Summary Card
 *
 * Displays project info with contextual actions based on pipeline bucket.
 * Actions vary: Generate WO, View WO, Mark Complete, etc.
 */

// Format date for display
function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Calculate days until ship date
function daysUntilShip(shipDate) {
  if (!shipDate) return null;
  const ship = new Date(shipDate);
  const today = new Date();
  const diffTime = ship - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

// Get urgency color based on days until ship
function getUrgencyColor(days) {
  if (days === null) return "text-gray-400";
  if (days < 0) return "text-red-600 font-semibold";
  if (days <= 3) return "text-orange-600 font-medium";
  if (days <= 7) return "text-yellow-600";
  return "text-gray-600";
}

// Bucket-specific action buttons
// IMPORTANT: Bucket IDs must match backend (see pmCommandCenter.js transformToPMPipelineBuckets)
function getCardActions(bucketId, card) {
  switch (bucketId) {
    case "pending_quotes":
      return [{ label: "View Quote", action: "view", primary: false }];
    case "unscheduled":
      // Won but missing ship date - can only view
      return [{ label: "View Details", action: "view", primary: false }];
    case "won_needs_wo":
      // Won quotes ready for PM - can assign and/or generate WO
      const actions = [];
      if (!card.assigned_pm_id && !card.project_manager_id) {
        actions.push({ label: "Assign to Me", action: "assign", primary: true });
      }
      if (card.order_number) {
        actions.push({ label: "Generate WO", action: "generate_wo", primary: !actions.length });
      }
      if (!actions.length) {
        actions.push({ label: "View Details", action: "view", primary: false });
      }
      return actions;
    case "in_production":
      // Active orders - can mark PDF ready or view progress
      return [
        { label: "PDF Ready", action: "pdf_ready", primary: true },
        { label: "View Progress", action: "view", primary: false },
      ];
    case "ready_closeout":
      return [
        { label: "Complete Close-Out", action: "closeout", primary: true },
      ];
    default:
      return [{ label: "View", action: "view", primary: false }];
  }
}

export default function PMProjectCard({
  card,
  bucketId,
  onSelect,
  onGenerateWorkOrder,
  onViewWorkOrder,
  onAssignToMe,
  onMarkPDFReady,
}) {
  const daysLeft = daysUntilShip(card.ship_date);
  const urgencyColor = getUrgencyColor(daysLeft);
  const actions = getCardActions(bucketId, card);

  // Handle action button click
  const handleAction = (action, e) => {
    e.stopPropagation();
    switch (action) {
      case "generate_wo":
        onGenerateWorkOrder?.();
        break;
      case "assign":
        onAssignToMe?.();
        break;
      case "pdf_ready":
        onMarkPDFReady?.();
        break;
      case "view":
        onSelect?.();
        break;
      case "closeout":
        onViewWorkOrder?.();
        break;
      default:
        onSelect?.();
    }
  };

  return (
    <div
      className="px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
      onClick={onSelect}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left: Project Info */}
        <div className="flex-1 min-w-0">
          {/* Quote/Order Number + Customer */}
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-sm text-purple-600 font-medium">
              {card.quote_number || card.wo_number}
            </span>
            {card.order_number && (
              <span className="text-xs text-gray-400 font-mono">
                ({card.order_number})
              </span>
            )}
            <span className="text-gray-300">|</span>
            <span className="text-sm font-medium text-gray-900 truncate">
              {card.customer_name}
            </span>
          </div>

          {/* Project Name/Description */}
          <div className="text-sm text-gray-700 truncate mb-2">
            {card.project_name || card.description || card.email_subject}
          </div>

          {/* Meta Row */}
          <div className="flex items-center gap-4 text-xs text-gray-500">
            {/* Ship Date */}
            {card.ship_date && (
              <span className={urgencyColor}>
                Ship: {formatDate(card.ship_date)}
                {daysLeft !== null && (
                  <span className="ml-1">
                    ({daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d`})
                  </span>
                )}
              </span>
            )}

            {/* SKU Count */}
            {(card.total_skus > 0 || card.total_line_items > 0) && (
              <span>
                {card.total_skus || card.total_line_items} SKU
                {(card.total_skus || card.total_line_items) !== 1 ? "s" : ""}
              </span>
            )}

            {/* Quantity */}
            {(card.total_qty > 0 || card.total_quantity > 0) && (
              <span>{card.total_qty || card.total_quantity} qty</span>
            )}

            {/* PM */}
            {(card.project_manager || card.assigned_pm_name) && (
              <span className="text-gray-400">
                PM: {card.project_manager || card.assigned_pm_name}
              </span>
            )}

            {/* Sales Rep */}
            {card.sales_rep && (
              <span className="text-gray-400">Sales: {card.sales_rep}</span>
            )}

            {/* Current Stage (for in_production) */}
            {card.current_stage && (
              <span
                className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                  card.current_stage === "PROD_ART"
                    ? "bg-purple-100 text-purple-700"
                    : card.current_stage === "PRINTING"
                      ? "bg-blue-100 text-blue-700"
                      : card.current_stage === "ASSEMBLY"
                        ? "bg-orange-100 text-orange-700"
                        : "bg-gray-100 text-gray-600"
                }`}
              >
                {card.current_stage}
              </span>
            )}

            {/* Confidence (for email intakes) */}
            {card.extraction_confidence !== undefined && (
              <span
                className={`${
                  card.extraction_confidence >= 0.8
                    ? "text-green-600"
                    : card.extraction_confidence >= 0.5
                      ? "text-yellow-600"
                      : "text-red-600"
                }`}
              >
                {Math.round(card.extraction_confidence * 100)}% confidence
              </span>
            )}
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {actions.map((action, idx) => (
            <button
              key={idx}
              onClick={(e) => handleAction(action.action, e)}
              className={`px-3 py-1.5 text-sm rounded transition-colors ${
                action.primary
                  ? "bg-purple-600 hover:bg-purple-700 text-white font-medium"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
